// src/components/chat/ToolCallDrawer.tsx
// Right-side drawer showing the full detail for a single llm_tool_calls row:
// data source + account, status meta, request payload, and the tool result.
// Result rendering is hybrid: a wiki page diff for edit_wiki_page rows,
// markdown for response_data with a `markdown` string field, JSON otherwise,
// summary as a fallback, and an empty state when nothing was stored.
// Driven by the `?tool=<toolCallId>` query param on /chat/:threadId.
//
// Shared presentational primitives (JSON/code/markdown blocks, badges, etc.)
// live in components/tool-calls/toolCallView — this file keeps only the
// chat-specific composition (sandbox body, wiki diff, result decision tree).

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Code2 } from 'lucide-react';
import { clsx } from 'clsx';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import Drawer from '@/components/ui/Drawer';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { getToolDisplay } from './toolDisplay';
import { llmToolCallApi } from '@/services/llm-tool-call-api';
import { useTheme } from '@/contexts/ThemeContext';
import type { LLMToolCallDetail, WikiRevisionContext } from '@/types/llmToolCall';
import {
  CodeBlockSection,
  JsonSection,
  KeyValue,
  MarkdownResultSection,
  Section,
  StatusBadge,
  ViewToggleButton,
} from '@/components/tool-calls/toolCallView';
import {
  formatBytes,
  formatTimestamp,
  getErrorMessage,
} from '@/components/tool-calls/toolCallFormat';

interface ToolCallDrawerProps {
  toolCallId: string | null;
  onClose: () => void;
}

const ToolCallDrawer: React.FC<ToolCallDrawerProps> = ({ toolCallId, onClose }) => {
  const isOpen = !!toolCallId;

  const { data, isLoading, error } = useQuery({
    queryKey: ['llm-tool-call', toolCallId],
    queryFn: () => llmToolCallApi.getToolCall(toolCallId!),
    enabled: isOpen,
  });

  const title = data ? getToolDisplay(data.toolName).label : 'Tool call';

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={title} width="lg">
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <AlertCircle className="w-10 h-10 text-destructive mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">
            Couldn't load tool call
          </p>
          <p className="text-xs text-muted-foreground">{getErrorMessage(error)}</p>
        </div>
      )}

      {data && <ToolCallBody detail={data} />}
    </Drawer>
  );
};

const ToolCallBody: React.FC<{ detail: LLMToolCallDetail }> = ({ detail }) => {
  if (detail.toolName === 'execute_python') {
    return <SandboxToolBody detail={detail} />;
  }

  const { icon: Icon } = getToolDisplay(detail.toolName);
  const isSuccess = detail.status === 'success';

  return (
    <div className="px-5 py-4 space-y-5">
      {/* Status + tool_name + timestamp */}
      <div className="flex items-center justify-between gap-3 pb-4 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <code className="text-xs font-mono text-muted-foreground truncate">
            {detail.toolName}
          </code>
        </div>
        <StatusBadge status={detail.status} />
      </div>

      {/* Context: data source + account */}
      <Section title="Context">
        <KeyValue label="Data source" value={detail.dataSource?.name ?? '—'} />
        <KeyValue label="Account" value={detail.dataSourceAccount?.name ?? '—'} />
      </Section>

      {/* Meta strip */}
      <Section title="Execution">
        <KeyValue label="Started" value={formatTimestamp(detail.createdAt)} />
        <KeyValue
          label="Duration"
          value={
            detail.executionTimeMs != null ? `${detail.executionTimeMs.toLocaleString()} ms` : '—'
          }
        />
        <KeyValue
          label="Rows"
          value={detail.rowCount != null ? detail.rowCount.toLocaleString() : '—'}
        />
      </Section>

      {/* Error block (only when failed) */}
      {!isSuccess && (detail.errorMessage || detail.errorCode) && (
        <Section title="Error">
          {detail.errorCode && <KeyValue label="Code" value={detail.errorCode} />}
          {detail.errorMessage && (
            <pre className="mt-2 text-xs text-destructive bg-destructive/15 rounded-md p-3 whitespace-pre-wrap break-words">
              {detail.errorMessage}
            </pre>
          )}
        </Section>
      )}

      {/* Request payload */}
      <JsonSection title="Request payload" value={detail.requestPayload} />

      {/* Result — hybrid: markdown if present, else JSON of full response_data,
          else fall back to response_summary, else empty state. */}
      <ResultSection detail={detail} />
    </div>
  );
};

/**
 * Sandbox-specific drawer body. Renders the input code, stdout, stderr,
 * exit code, and any workspace files produced — instead of dumping the full
 * tool result as escaped JSON.
 */
interface SandboxResult {
  exit_code?: number;
  stdout?: string;
  stderr?: string;
  truncated?: boolean;
  duration_ms?: number;
  outputs?: Array<{ handle: string; bytes: number; action: 'created' | 'updated' | 'deleted' }>;
}

const SandboxToolBody: React.FC<{ detail: LLMToolCallDetail }> = ({ detail }) => {
  const isSuccess = detail.status === 'success';
  const code =
    detail.requestPayload && typeof detail.requestPayload.code === 'string'
      ? detail.requestPayload.code
      : '';
  const result: SandboxResult = (detail.responseData ?? {}) as SandboxResult;
  const stderr = result.stderr ?? '';

  return (
    <div className="px-5 py-4 space-y-5">
      <div className="flex items-center justify-between gap-3 pb-4 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <Code2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <code className="text-xs font-mono text-muted-foreground truncate">
            {detail.toolName}
          </code>
        </div>
        <StatusBadge status={detail.status} />
      </div>

      <Section title="Execution">
        <KeyValue label="Started" value={formatTimestamp(detail.createdAt)} />
        <KeyValue
          label="Duration"
          value={
            result.duration_ms != null
              ? `${result.duration_ms.toLocaleString()} ms`
              : detail.executionTimeMs != null
                ? `${detail.executionTimeMs.toLocaleString()} ms`
                : '—'
          }
        />
        <KeyValue
          label="Exit code"
          value={result.exit_code != null ? String(result.exit_code) : '—'}
        />
        {result.truncated && <KeyValue label="Truncated" value="Yes — output was capped" />}
      </Section>

      {!isSuccess && detail.errorMessage && (
        <Section title="Error">
          <pre className="mt-2 text-xs text-destructive bg-destructive/15 rounded-md p-3 whitespace-pre-wrap break-words">
            {detail.errorMessage}
          </pre>
        </Section>
      )}

      <CodeBlockSection title="Code" code={code} language="python" />
      <CodeBlockSection title="stdout" code={result.stdout || '(empty)'} />
      {stderr && <CodeBlockSection title="stderr" code={stderr} variant="error" />}

      {result.outputs && result.outputs.length > 0 && (
        <Section title="Workspace outputs">
          {result.outputs.map((o, i) => (
            <div
              key={i}
              className="flex items-baseline gap-3 text-sm"
            >
              <span
                className={clsx(
                  'w-20 flex-shrink-0 text-xs font-medium uppercase tracking-wide',
                  o.action === 'created'
                    ? 'text-success'
                    : o.action === 'updated'
                      ? 'text-info'
                      : 'text-destructive',
                )}
              >
                {o.action}
              </span>
              <code className="text-xs text-foreground break-all">
                {o.handle}
              </code>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {formatBytes(o.bytes)}
              </span>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
};

/**
 * Decides what to show under "Result" based on what's available in the row:
 *  1. wikiRevision present → render before/after diff (edit_wiki_page only)
 *  2. responseData with a `markdown` string → render markdown (toggle to JSON)
 *  3. responseData (any other shape) → JSON
 *  4. only responseSummary present → JSON of summary, labeled as such
 *  5. nothing → empty state
 */
const ResultSection: React.FC<{ detail: LLMToolCallDetail }> = ({ detail }) => {
  const { responseData, responseSummary, wikiRevision } = detail;

  if (wikiRevision) {
    return <WikiRevisionResultSection revision={wikiRevision} />;
  }

  const markdown =
    responseData && typeof responseData.markdown === 'string' ? responseData.markdown : null;

  if (markdown) {
    return <MarkdownResultSection markdown={markdown} fullPayload={responseData} />;
  }

  if (responseData) {
    return <JsonSection title="Result" value={responseData} />;
  }

  if (responseSummary) {
    return (
      <section>
        <p className="text-xs text-muted-foreground mb-2 italic">
          Full result not stored for this tool — showing summary.
        </p>
        <JsonSection title="Result summary" value={responseSummary} />
      </section>
    );
  }

  return (
    <section>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
        Result
      </h3>
      <p className="text-sm text-muted-foreground italic">
        No result was stored for this tool call.
      </p>
    </section>
  );
};

/**
 * Renders the before/after diff for a wiki page edit. Used when the
 * tool-call row is `edit_wiki_page` and we successfully resolved the
 * revision snapshot. User can toggle split/unified view.
 */
const WikiRevisionResultSection: React.FC<{ revision: WikiRevisionContext }> = ({ revision }) => {
  const { isDark } = useTheme();
  const [splitView, setSplitView] = useState(true);

  return (
    <section>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Result — page diff
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            <span className="font-medium text-foreground">
              {revision.pageTitle}
            </span>
            <span className="mx-1.5">·</span>
            v{revision.preEditVersion} → v{revision.postEditVersion}
          </p>
        </div>
        <div
          className="inline-flex rounded-md border border-border overflow-hidden text-xs flex-shrink-0"
          role="tablist"
          aria-label="Diff view"
        >
          <ViewToggleButton active={splitView} onClick={() => setSplitView(true)}>
            Split
          </ViewToggleButton>
          <ViewToggleButton active={!splitView} onClick={() => setSplitView(false)}>
            Unified
          </ViewToggleButton>
        </div>
      </div>
      <div className="rounded-md border border-border overflow-hidden text-xs">
        <ReactDiffViewer
          oldValue={revision.beforeMarkdown}
          newValue={revision.afterMarkdown}
          splitView={splitView}
          compareMethod={DiffMethod.WORDS}
          useDarkTheme={isDark}
          leftTitle={`v${revision.preEditVersion} (before)`}
          rightTitle={`v${revision.postEditVersion} (after)`}
        />
      </div>
    </section>
  );
};

export default ToolCallDrawer;
